import { __assign, __read } from "tslib";
import React, { useRef, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Tooltip, BulletList } from '@keen.io/ui-core';
import { useTooltip } from '@keen.io/react-hooks';
import { calculateTotalValue } from '@keen.io/charts-utils';
import PieSlice from './pie-slice.component';
import ShadowFilter from '../../components/shadow-filter.component';
import { TooltipItem, generateCircularChart, getCircularChartTooltipContent, } from '../circular-chart';
import { ChartBase, Delayed } from '../../components';
import { theme as defaultTheme } from '../../theme';
import { TOOLTIP_MOTION } from '../../constants';
export var PieChart = function (_a) {
    var data = _a.data, svgDimensions = _a.svgDimensions, _b = _a.theme, theme = _b === void 0 ? defaultTheme : _b, _c = _a.margins, margins = _c === void 0 ? { top: 30, right: 20, bottom: 30, left: 40 } : _c, _d = _a.labelSelector, labelSelector = _d === void 0 ? 'name' : _d, _e = _a.keys, keys = _e === void 0 ? ['value'] : _e, _f = _a.disabledLabels, disabledLabels = _f === void 0 ? [] : _f, _g = _a.padAngle, padAngle = _g === void 0 ? 0.02 : _g, _h = _a.padRadius, padRadius = _h === void 0 ? 100 : _h, _j = _a.cornerRadius, cornerRadius = _j === void 0 ? 2 : _j, _k = _a.innerRadius, innerRadius = _k === void 0 ? 0.1 : _k, _l = _a.labelsRadius, labelsRadius = _l === void 0 ? 30 : _l, _m = _a.labelsPosition, labelsPosition = _m === void 0 ? 'inside' : _m, _o = _a.labelsAutocolor, labelsAutocolor = _o === void 0 ? true : _o, _p = _a.stackTreshold, stackTreshold = _p === void 0 ? 4 : _p, onFinalDataStack = _a.onFinalDataStack, _q = _a.tooltipSettings, tooltipSettings = _q === void 0 ? {} : _q, activeKey = _a.activeKey, _r = _a.valueMode, valueMode = _r === void 0 ? 'percentage' : _r, dataSeriesOffset = _a.dataSeriesOffset;
    var _s = __read(useState(function () {
        if (!stackTreshold)
            return 0;
        var total = calculateTotalValue(data, labelSelector, keys);
        return total * (stackTreshold / 100);
    }), 1), treshold = _s[0];
    var _t = generateCircularChart({
        data: data,
        margins: margins,
        padAngle: padAngle,
        padRadius: padRadius,
        cornerRadius: cornerRadius,
        innerRadius: innerRadius,
        labelSelector: labelSelector,
        labelsRadius: labelsRadius,
        keys: keys,
        disabledLabels: disabledLabels,
        labelsPosition: labelsPosition,
        dimension: svgDimensions,
        colors: theme.colors,
        treshold: treshold,
        formatValue: tooltipSettings.formatValue,
        dataSeriesOffset: dataSeriesOffset,
    }), arcs = _t.arcs, drawArc = _t.drawArc, sortedDataSeries = _t.sortedDataSeries;
    useEffect(function () {
        onFinalDataStack && onFinalDataStack(sortedDataSeries);
    }, []);
    var svgElement = useRef(null);
    var _u = useTooltip(svgElement), tooltipVisible = _u.tooltipVisible, tooltipPosition = _u.tooltipPosition, tooltipSelectors = _u.tooltipSelectors, updateTooltipPosition = _u.updateTooltipPosition, hideTooltip = _u.hideTooltip;
    var themeTooltipSettings = theme.tooltip;
    return (React.createElement(React.Fragment, null,
        React.createElement(AnimatePresence, null, tooltipVisible && (React.createElement(motion.div, __assign({}, TOOLTIP_MOTION, { initial: { opacity: 0, x: tooltipPosition.x, y: tooltipPosition.y }, animate: {
                x: tooltipPosition.x,
                y: tooltipPosition.y,
                opacity: 1,
            }, style: {
                position: 'absolute',
                pointerEvents: 'none',
            } }),
            React.createElement(Tooltip, { mode: themeTooltipSettings.mode, hasArrow: false }, tooltipSelectors && (React.createElement(BulletList, { items: getCircularChartTooltipContent({
                    data: data,
                    keys: keys,
                    labelSelector: labelSelector,
                    selectors: tooltipSelectors,
                    disabledLabels: disabledLabels,
                    formatValue: tooltipSettings.formatValue,
                    valueMode: valueMode,
                }), renderItem: function (_idx, item) { return (React.createElement(TooltipItem, { data: item.data, theme: theme })); } })))))),
        React.createElement(Delayed, null,
            React.createElement(ChartBase, { ref: svgElement, theme: theme, svgDimensions: svgDimensions, margins: margins },
                React.createElement("g", { style: {
                        transform: "translate(" + svgDimensions.width / 2 + "px, " + svgDimensions.height / 2 + "px)",
                    } },
                    React.createElement(ShadowFilter, null),
                    React.createElement(AnimatePresence, null, arcs.map(function (_a) {
                        var labelNumeric = _a.labelNumeric, labelPercentage = _a.labelPercentage, labelPosition = _a.labelPosition, activePosition = _a.activePosition, dataKey = _a.dataKey, startAngle = _a.startAngle, endAngle = _a.endAngle, color = _a.color, selector = _a.selector, stacked = _a.stacked, stack = _a.stack;
                        return (React.createElement(PieSlice, { key: dataKey, id: dataKey, activeKey: activeKey, draw: drawArc, startAngle: startAngle, endAngle: endAngle, label: valueMode === 'percentage'
                                ? labelPercentage
                                : labelNumeric, autocolor: labelsAutocolor, activePosition: activePosition, labelPosition: labelPosition, background: color, onMouseMove: function (e) {
                                if (themeTooltipSettings.enabled) {
                                    if (stacked)
                                        updateTooltipPosition(e, stack);
                                    else
                                        updateTooltipPosition(e, [{ color: color, selector: selector }]);
                                }
                            }, onMouseLeave: function () { return hideTooltip(); }, dataSeriesOffset: dataSeriesOffset }));
                    })))))));
};
export default PieChart;
